/**
 * Roles, what they grant, and who holds them (`NXP-AUTHZ-001`).
 *
 * Seven operations, decided separately by the platform, consumed separately
 * here. Reading which roles exist, reading what a role grants and reading who
 * holds what are three different authorities, and a principal can hold any one
 * without the others — so no section on this page infers anything from another,
 * and reaching the page implies nothing at all.
 *
 * That restraint is the whole point. The tempting shortcut is to decide once
 * that somebody is "an authorization administrator" and render everything; it
 * would be a second authorization model, living in a browser, disagreeing with
 * the first the day a grant changes.
 *
 * Two more things this screen refuses to blur:
 *
 * **Permission is not availability.** A control absent because the *target*
 * forbids it — a system role cannot be granted anything more — says so, rather
 * than looking like the caller was refused.
 *
 * **A projection is not a grant.** Every control here was offered on what the
 * decision point said a moment ago. The route decides again, and when it
 * refuses, the refusal is what gets rendered.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type {
  Page,
  PermissionScope,
  PrincipalSummary,
  Role,
  RoleAssignment,
  RoleGrant,
} from "../api/types";
import { usePagedCollection, type PagedCollection } from "../api/useCollection";
import {
  Banner,
  Card,
  Identifier,
  Mono,
  More,
  Pill,
  when,
} from "../components/primitives";
import { change, Result, type Outcome } from "../components/governed";
import { Empty, Forbidden, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

/**
 * The root of trust, by the key the deployment bootstrap writes.
 *
 * Mirrored from `nexus.authorization.root_of_trust.ROOT_ADMINISTRATOR_ROLE`
 * deliberately, and only to avoid offering a control the API will certainly
 * refuse. It is not what protects the root: the API refuses to assign or revoke
 * it whatever this file believes, and if this constant ever drifted the result
 * would be a refusal rendered honestly rather than a root that could be taken.
 */
const ROOT_ROLE = "authorization_administrator";

/**
 * The three administrative authorities a root may appoint, by the keys the
 * platform gives them (`nexus.authorization.root_of_trust`,
 * `nexus.authorization.contracts.service_identity` and
 * `nexus.authorization.contracts.access_policy`).
 *
 * Three authorities, not three sizes of one. The first decides who holds which
 * operation; the second decides which restricted data those operations reach;
 * the third decides which services exist to be principals at all. Somebody
 * holding more than one holds that many appointments, each granted, audited and
 * ended on its own. Mirrored for the same reason as `ROOT_ROLE` — to label and
 * to avoid offering what the API refuses — and never as what protects anything.
 */
const DELEGATED_ROLE = "delegated_authorization_administrator";
const SERVICE_IDENTITY_ADMINISTRATOR_ROLE = "service_identity_administrator";
const ACCESS_POLICY_ADMINISTRATOR_ROLE = "access_policy_administrator";

/**
 * Who administers authorization is its own authority, held by the root alone.
 *
 * The capability keys the decision point is asked about. They are not held by a
 * delegate, which is the V1 delegation boundary rendered: only the root of
 * trust decides who administers.
 */
const ADMINISTRATIVE_ASSIGN = "authorization.administrative_role_assignment.assign";
const ADMINISTRATIVE_REVOKE = "authorization.administrative_role_assignment.revoke";

function isAdministrative(role: Role): boolean {
  return role.permission_kind === "administrative";
}

/**
 * Which administrative authority a role is, in words.
 *
 * Said wherever a role is listed, so the service identity administrator can
 * never be read as a flavour of the authorization administrator — both are
 * `administrative`, and that shared kind is exactly why the label has to name
 * the difference.
 */
function authorityOf(role: Role): string | null {
  if (!isAdministrative(role)) return null;
  if (role.key === ROOT_ROLE) return "Authorization — root of trust";
  if (role.key === DELEGATED_ROLE) return "Authorization — delegated";
  if (role.key === SERVICE_IDENTITY_ADMINISTRATOR_ROLE) return "Service identities";
  if (role.key === ACCESS_POLICY_ADMINISTRATOR_ROLE) return "Access policies";
  return "Administrative";
}

/** The permission kind, and for an administrative role which authority it is. */
function KindOf({ role }: { role: Role }) {
  const authority = authorityOf(role);
  return (
    <>
      <Pill tone={isAdministrative(role) ? "mind" : "quiet"}>
        {role.permission_kind.replace(/_/g, " ")}
      </Pill>
      {authority === null ? null : (
        <span className="nx-table__secondary" style={{ display: "block", marginTop: 4 }}>
          {authority}
        </span>
      )}
    </>
  );
}

/**
 * The scopes a grant may be conferred at, in ladder order.
 *
 * `own` is deliberately absent (`NXP-AUTHZ-OWN-SCOPE-001`). It is still a
 * `PermissionScope`, because a grant conferred before it was withdrawn still
 * has to render in the list below — this is the *offer*, not the vocabulary.
 * The API refuses it either way; a dropdown that kept offering it would be
 * inviting administrators to confer an authority that reaches nothing and to
 * receive a refusal for having accepted what the screen suggested.
 */
const SCOPES: PermissionScope[] = ["tenant", "organization", "resource"];

/**
 * What a scope is called on screen.
 *
 * Only `resource` is renamed. "resource" alone reads as implementation
 * vocabulary and does not say the thing that matters about it — that the grant
 * is confined to one named thing rather than to a type. Every other scope keeps
 * the wording it already had, so an existing grant row reads exactly as it did
 * before this story.
 *
 * That includes `own`, which can no longer be conferred but can still be held.
 * It is labelled as what it is rather than hidden: a row an administrator
 * cannot explain is worse than one that says plainly why the permission beside
 * it does nothing.
 */
function scopeLabel(scope: string): string {
  if (scope === "resource") return "Named resource";
  if (scope === "own") return "own (withdrawn — reaches nothing)";
  return scope;
}

/**
 * One collection of this screen's, read a page at a time.
 *
 * The paging is the shared primitive's — what page came back, whether the next
 * one appends, what a refresh means. What stays here is this screen's own
 * business: which path, fifty at a time, and that a different path is a
 * different collection rather than a continuation of the last one, so asking
 * about a different role starts at the beginning instead of showing the
 * previous role's grants under the new role's heading.
 *
 * The primitive reads pages as `PageOf`, which is narrower than the `Page` the
 * API returns: it does not carry `projection`. Nothing is discarded by that
 * here, and the proof is worth writing down because the next reader will ask.
 * All three authorization endpoints answer with `Page.of(...)`, which never
 * sets `projection`; the field defaults to `None`; and the only code that ever
 * constructs a degraded or unprojectable page is the outbox service, which
 * does not serve these routes. So `projection` is null on every page this
 * screen reads. If authorization ever starts projecting, this is the line that
 * would have to stop throwing it away.
 */
function useCollection<T>(path: string, enabled: boolean): PagedCollection<T> {
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<T>>(path, { params: { limit: 50, cursor }, signal }),
    [path],
  );
  return usePagedCollection<T>(readPage, { enabled, resetKey: path });
}

/**
 * The people matching what an administrator is typing.
 *
 * Its own read rather than `useCollection`, because this is a *search* and not a
 * collection: it starts empty, changes with every keystroke, and is deliberately
 * one page deep — somebody who has typed three letters and sees fifty names
 * types a fourth rather than paging.
 *
 * **Nothing is read until something is typed.** An empty box is not a search,
 * and asking on mount would read a `PII` collection for every administrator who
 * opens this screen.
 *
 * A failed or refused read is an empty list and nothing else. The field still
 * accepts a pasted identifier, so a directory that cannot be reached degrades to
 * exactly what this screen offered before there was one — and the assignment
 * itself is decided by the route regardless of anything shown here.
 */
function usePrincipalSearch(looking: string, enabled: boolean): PrincipalSummary[] {
  // What came back, and the question it answered. Keeping the two together is
  // what makes the empty case *derived* rather than written: an effect that
  // cleared this synchronously would both trip the cascading-render rule and,
  // more to the point, leave one query's people on screen while the next was
  // still in flight.
  const [answered, setAnswered] = useState<{ question: string; people: PrincipalSummary[] }>({
    question: "",
    people: [],
  });
  const wanted = looking.trim();

  useEffect(() => {
    if (!enabled || wanted === "") return;
    const controller = new AbortController();
    // Typing is faster than the network, and every keystroke would be a read of
    // somebody's personal data. The last thing typed is the one asked about.
    const asking = setTimeout(() => {
      void api
        .get<Page<PrincipalSummary>>("/identity/principals", {
          params: { named: wanted, limit: 20 },
          signal: controller.signal,
        })
        .then((page) => {
          setAnswered({ question: wanted, people: page.items });
        })
        .catch(() => {
          // Refused, failed, or aborted. An empty answer to this question, and
          // the field still takes a pasted identifier — which is exactly what
          // this screen offered before there was a directory to read.
          setAnswered({ question: wanted, people: [] });
        });
    }, 150);
    return () => {
      clearTimeout(asking);
      controller.abort();
    };
  }, [wanted, enabled]);

  return answered.question === wanted ? answered.people : [];
}

/**
 * Choosing the principal an appointment is for.
 *
 * One idiom wherever somebody is appointed — an ordinary role or an
 * administrative authority — so the two cannot drift into different answers
 * about what was typed and what is sent. What was typed and what will be sent
 * are different things the moment a directory exists: the administrator types
 * a name and the API is given an identifier. The caller keeps both, because
 * the form that owns them decides when they are cleared.
 */
function PrincipalPicker({
  id,
  label,
  looking,
  chosen,
  onType,
  onChoose,
}: {
  id: string;
  label: string;
  looking: string;
  chosen: string;
  onType: (text: string) => void;
  onChoose: (person: PrincipalSummary) => void;
}) {
  const session = useSession();
  /**
   * Whether this caller may read the directory at all.
   *
   * Its own authority and a `PII` one (`NXP-AUTHZ-PRINCIPAL-DISCOVERY-001`), so
   * holding every role-administration capability answers nothing about it. The
   * projection decides whether to *offer* the chooser; the route decides every
   * actual read, and a refusal renders as an answer like any other.
   */
  const mayFindPeople = session.may("principal.read");
  const found = usePrincipalSearch(looking, mayFindPeople);

  return (
    <div className="nx-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="nx-input"
        required
        value={looking}
        onChange={(event) => {
          // Typing replaces both what is shown and what will be sent, because
          // text that no longer names the chosen person must not silently keep
          // their identifier attached to the form.
          onType(event.target.value);
        }}
      />
      {mayFindPeople && found.length > 0 ? (
        <ul
          role="listbox"
          aria-label="People found"
          style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}
        >
          {found.map((person) => (
            <li key={person.user_id}>
              <button
                type="button"
                role="option"
                aria-selected={chosen === person.user_id}
                className="nx-button"
                style={{ width: "100%", textAlign: "left" }}
                onClick={() => {
                  // The name goes in the box; the identifier goes to the API.
                  onChoose(person);
                }}
              >
                {person.display_name}
                {person.status === "active" ? "" : ` — ${person.status}`}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <span className="nx-hint">
        {mayFindPeople
          ? "Type a name to find somebody, or paste an identifier you already have. Somebody who has left is still listed and says so — they can still hold an appointment, and appointing them again is rarely what was meant."
          : "The principal identifier. Reading who exists is an authority of its own, and you do not hold it — so this comes from wherever the principal is already identified: an audit record, or the identity provider."}
      </span>
    </div>
  );
}

// ── roles ─────────────────────────────────────────────────────────────────

function CreateRole({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({ key: "", displayName: "" });
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  return (
    <form
      style={{ padding: "0 18px 18px" }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            // Exactly the fields the API declares. It forbids every other one,
            // which is how a body naming who acted is refused rather than
            // quietly ignored.
            api.post<Role>(
              "/authorization/roles",
              { key: form.key, display_name: form.displayName },
              { correlationId },
            ),
          "Defining a role",
          `Role ${form.key} defined.`,
        )
          .then((result) => {
            setOutcome(result);
            if (result.tone === "ok") {
              setForm({ key: "", displayName: "" });
              onCreated();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Define a role</h3>
      <div className="nx-field">
        <label htmlFor="role-key">Key</label>
        <input
          id="role-key"
          className="nx-input"
          required
          maxLength={64}
          value={form.key}
          onChange={(event) => {
            setForm({ ...form, key: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="role-name">Display name</label>
        <input
          id="role-name"
          className="nx-input"
          required
          maxLength={128}
          value={form.displayName}
          onChange={(event) => {
            setForm({ ...form, displayName: event.target.value });
          }}
        />
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Define
      </button>
      <p className="nx-hint" style={{ marginTop: 8 }}>
        Roles defined here are ordinary, non-system and data-access. They cannot administer
        authorization: the engine refuses an operation held under the wrong permission kind, so
        granting one of these a role, permission or assignment operation would never take effect.
      </p>
      <Result outcome={outcome} />
    </form>
  );
}

function Roles({
  roles,
  selectedId,
  onSelect,
}: {
  roles: PagedCollection<Role>;
  selectedId: string | null;
  onSelect: (roleId: string) => void;
}) {
  const session = useSession();
  if (!session.may("authorization.role.read")) {
    return (
      <Card title="Roles">
        <Forbidden what="the roles in this tenant" />
      </Card>
    );
  }

  return (
    <Card
      title="Roles"
      actions={
        <button type="button" className="nx-button" onClick={roles.refresh}>
          Refresh
        </button>
      }
      bodyless
    >
      <Show
        resource={roles.state}
        what="the roles in this tenant"
        onRetry={roles.refresh}
        empty={() =>
          roles.items.length === 0 ? (
            <Empty
              title="No roles are defined."
              hint="A tenant whose authorization has never been established has none — the platform denies by default."
            />
          ) : null
        }
      >
        {() => (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <caption className="nx-visually-hidden">Roles defined in this tenant</caption>
              <thead>
                <tr>
                  <th scope="col">Role</th>
                  <th scope="col">Permission kind</th>
                  <th scope="col">Origin</th>
                  <th scope="col">Grants</th>
                </tr>
              </thead>
              <tbody>
                {roles.items.map((role) => (
                  <tr key={role.role_id}>
                    <td>
                      <span className="nx-table__primary">{role.display_name}</span>
                      <span className="nx-table__secondary" style={{ display: "block" }}>
                        <Mono>{role.key}</Mono>
                      </span>
                    </td>
                    <td>
                      <KindOf role={role} />
                    </td>
                    <td>
                      {role.key === ROOT_ROLE ? (
                        <span title="Established by deployment, not by request">
                          root of trust
                        </span>
                      ) : role.system_role ? (
                        "seeded"
                      ) : (
                        "defined here"
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="nx-button"
                        aria-pressed={selectedId === role.role_id}
                        onClick={() => {
                          onSelect(role.role_id);
                        }}
                      >
                        {selectedId === role.role_id ? "Showing" : "Show"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Show>
      <More
        collection={roles}
        label="Show more roles"
        hint="This is one page of the roles defined here, not all of them."
        placement="bodyless"
      />
      {session.may("authorization.role.create") ? <CreateRole onCreated={roles.refresh} /> : null}
    </Card>
  );
}

// ── what a role grants ────────────────────────────────────────────────────

function AddGrant({ role, onAdded }: { role: Role; onAdded: () => void }) {
  const [form, setForm] = useState({
    resourceType: "",
    operation: "",
    scope: "tenant" as PermissionScope,
    resourceId: "",
  });
  const confined = form.scope === "resource";
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  return (
    <form
      style={{ padding: "0 18px 18px" }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<RoleGrant>(
              `/authorization/roles/${role.role_id}/permissions`,
              {
                resource_type: form.resourceType,
                operation: form.operation,
                scope: form.scope,
                // Sent only where it means something. The API rejects a
                // `resource_id` at any other scope — and rightly: an
                // identifier attached to a grant that does not read it would
                // look like a confinement that is not being enforced.
                ...(confined ? { resource_id: form.resourceId } : {}),
              },
              { correlationId },
            ),
          "Granting an operation",
          confined
            ? `${role.key} now grants ${form.operation} on the ${form.resourceType} named ${form.resourceId}.`
            : `${role.key} now grants ${form.operation} on ${form.resourceType}.`,
        )
          .then((result) => {
            setOutcome(result);
            if (result.tone === "ok") {
              setForm({ ...form, resourceType: "", operation: "", resourceId: "" });
              onAdded();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Grant an operation</h3>
      <div className="nx-field">
        <label htmlFor="grant-resource">Resource type</label>
        <input
          id="grant-resource"
          className="nx-input"
          required
          maxLength={128}
          value={form.resourceType}
          onChange={(event) => {
            setForm({ ...form, resourceType: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="grant-operation">Operation</label>
        <input
          id="grant-operation"
          className="nx-input"
          required
          maxLength={64}
          value={form.operation}
          onChange={(event) => {
            setForm({ ...form, operation: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="grant-scope">Scope</label>
        <select
          id="grant-scope"
          className="nx-select"
          value={form.scope}
          onChange={(event) => {
            const scope = event.target.value as PermissionScope;
            // The identifier does not survive a change of scope. Keeping it
            // would let a value typed for one grant be sent with the next, and
            // a stale confinement is the kind of mistake nobody reviews for.
            setForm({ ...form, scope, resourceId: scope === "resource" ? form.resourceId : "" });
          }}
        >
          {SCOPES.map((scope) => (
            <option key={scope} value={scope}>
              {scopeLabel(scope)}
            </option>
          ))}
        </select>
        <span className="nx-hint">
          Scope is the security context the authority is exercised in. Tenant scope does not require
          the thing acted on to belong to the tenant. A named resource is the narrowest there is: the
          grant reaches that one resource and nothing else of its type.
        </span>
      </div>
      {confined ? (
        <div className="nx-field">
          <label htmlFor="grant-resource-id">Resource identifier</label>
          <input
            id="grant-resource-id"
            className="nx-input nx-mono"
            required
            minLength={1}
            maxLength={255}
            value={form.resourceId}
            aria-describedby="grant-resource-id-hint"
            onChange={(event) => {
              setForm({ ...form, resourceId: event.target.value });
            }}
          />
          <span className="nx-hint" id="grant-resource-id-hint">
            Enter the resource identifier this permission is confined to. Authorization records the
            identifier and does not look it up — the service that owns the resource checks it when
            the action happens, so this form cannot tell you whether anything with that identifier
            exists.
          </span>
        </div>
      ) : null}
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Grant
      </button>
      <Result outcome={outcome} />
    </form>
  );
}

function Grants({ role }: { role: Role | null }) {
  const session = useSession();
  const mayRead = session.may("authorization.role_permission.read");
  const mayAdd = session.may("authorization.role_permission.add");
  const grants = useCollection<RoleGrant>(
    role === null ? "/authorization/roles/none/permissions" : `/authorization/roles/${role.role_id}/permissions`,
    mayRead && role !== null,
  );

  if (role === null) {
    return (
      <Card title="What a role grants">
        <p className="nx-hint">Choose a role to see what it grants.</p>
      </Card>
    );
  }

  return (
    <Card title={`What ${role.display_name} grants`} bodyless>
      {mayRead ? (
        <>
          <Show
            resource={grants.state}
            what="what this role grants"
            onRetry={grants.refresh}
            empty={() =>
              grants.items.length === 0 ? (
                <Empty title="This role grants nothing." hint="It confers no authority at all." />
              ) : null
            }
          >
            {() => (
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <caption className="nx-visually-hidden">Operations this role grants</caption>
                  <thead>
                    <tr>
                      <th scope="col">Resource type</th>
                      <th scope="col">Operation</th>
                      <th scope="col">Scope</th>
                      <th scope="col">Resource</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Keyed by `permission_id`, which is what keeps two grants
                        differing only in the resource they name from collapsing
                        into one row. Same role, same type, same operation,
                        different resource: two permissions, and each has to
                        stay separately visible or revoking the wrong one
                        becomes possible. */}
                    {grants.items.map((grant) => (
                      <tr key={grant.permission_id}>
                        <td>
                          <Mono>{grant.resource_type}</Mono>
                        </td>
                        <td>
                          <Mono>{grant.operation}</Mono>
                        </td>
                        <td>{scopeLabel(grant.scope)}</td>
                        <td>
                          {grant.resource_id != null && grant.resource_id !== "" ? (
                            // Never the identifier on its own — it means
                            // nothing without the type in the same row, which
                            // is why this column sits beside that one rather
                            // than replacing it.
                            <Mono>{grant.resource_id}</Mono>
                          ) : (
                            <span className="nx-hint">
                              <span aria-hidden="true">—</span>
                              <span className="nx-visually-hidden">
                                Not confined to one resource
                              </span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Show>
          <More
            collection={grants}
            label="Show more grants"
            hint="This is one page of what this role grants, not all of it."
            placement="bodyless"
          />
        </>
      ) : (
        <div className="nx-card__body">
          <Forbidden what="what a role grants" />
          <p className="nx-hint">
            Reading a role&apos;s grants is granted apart from reading which roles exist. Holding one
            does not confer the other.
          </p>
        </div>
      )}

      {/* Absent because of the target, not the caller — and it says which. */}
      {role.system_role ? (
        <p className="nx-hint" style={{ padding: "0 18px 18px" }}>
          {role.key === ROOT_ROLE
            ? "The root of trust is established by deployment. Its grants are a fixed manifest and nothing may be added to them here."
            : "This is a seeded system role. Its grants are written once at bootstrap and cannot be changed — including by somebody who may grant operations."}
        </p>
      ) : mayAdd ? (
        <AddGrant role={role} onAdded={grants.refresh} />
      ) : null}

      {!mayRead && mayAdd && !role.system_role ? (
        <p className="nx-hint" style={{ padding: "0 18px 12px" }}>
          You may grant an operation to this role but not read what it already grants, so what is
          below is not shown.
        </p>
      ) : null}
    </Card>
  );
}

// ── who holds what ────────────────────────────────────────────────────────

/**
 * Said wherever roles are offered to choose from and the platform has said
 * there are more of them than were read.
 *
 * A list one page deep is not the roles there are, and a chooser that does not
 * say so is a completeness claim it has not earned: somebody who cannot find a
 * role they know exists would conclude it does not.
 */
function MoreRolesThanListed() {
  return (
    <span className="nx-hint">
      There are more roles than are listed here. Show more of them under Roles above to choose one
      that is not in this list.
    </span>
  );
}

/**
 * Why there is no role to choose from — said from the answer, not from the
 * projection that predicted it.
 *
 * The temptation here is `session.may("authorization.role.read")`: the caller
 * appears to hold the reading, so an empty list must mean an empty tenant.
 * That inference is this file's own warning made concrete. The capability
 * report is a projection of a decision taken a moment ago; the route decides
 * again, and when it refuses — or fails, or has simply not answered yet — the
 * list is empty for a reason that says nothing whatever about what exists.
 * Only a page that actually arrived has earned "there is none".
 *
 * The projection is still consulted for the one thing it does answer for: it
 * is why the read was never attempted, and saying so is the difference between
 * "you were not allowed to look" and "there is nothing there".
 */
function whyNothingToChoose(mayReadRoles: boolean, roles: PagedCollection<Role>): string {
  if (!mayReadRoles) {
    return "A role identifier, because reading which roles exist is a separate authority you do not hold.";
  }
  if (roles.state.kind !== "ready") {
    // Refused, failed, or still going — the Roles card above renders which.
    return "A role identifier. No role that may be given has been read.";
  }
  return "A role identifier. There is no role here to choose from.";
}

function Assign({
  roles,
  held,
  onAssigned,
}: {
  roles: PagedCollection<Role>;
  held: RoleAssignment[];
  onAssigned: () => void;
}) {
  const session = useSession();
  // `organizationId` is empty for a tenant-wide appointment, which is the
  // default and the common case.
  const [form, setForm] = useState({ principalId: "", roleId: "", organizationId: "" });
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // Ordinary system roles stay assignable (`NXP-AUTHZ-001A`). Only the root is
  // not, because an administrator who could hand it over could hand over the
  // authority that governs them.
  //
  // No administrative role is offered here either. Who administers — the
  // delegated authorization administrator, the service identity administrator
  // — is appointed under Administrative appointments by a separate authority,
  // and the ordinary route refuses every administrative role whatever this list
  // offers. Offering them would be inviting a refusal the screen already knows.
  const assignable = roles.items.filter(
    (role) => role.key !== ROOT_ROLE && !isAdministrative(role),
  );

  /**
   * The organizations this screen can name, which are the caller's own.
   *
   * Nexus publishes no directory of organizations, and `/identity/me` already
   * says which ones the caller belongs to and what they are called
   * (`NXP-IDENTITY-ORG-NAME-001`). That is a smaller set than the tenant's, and
   * deliberately: it is what this browser can honestly name. An administrator
   * appointing somebody in an organization they are not themselves in has the
   * API for it, and would be refused here by nothing this screen invented.
   *
   * The assignee's membership is not checked here and could not be — the
   * screen knows the caller's memberships, not theirs. The API checks it before
   * anything is written, and its refusal renders as an answer like any other.
   */
  const organizations = session.principal.organization_memberships;

  /**
   * What was typed, kept apart from what will be sent.
   *
   * They are different things the moment a directory exists: the administrator
   * types a name and the API is given an identifier. Conflating them is how a
   * screen sends "Ada" as a `principal_id`.
   */
  const [looking, setLooking] = useState("");

  const already = held.find(
    (assignment) =>
      assignment.active &&
      assignment.principal_id === form.principalId &&
      assignment.role_id === form.roleId &&
      // The organization is part of which authority this is, not a detail of
      // it. The same role in a different organization is a different
      // appointment, and refusing it as a duplicate would make the rung
      // `NXP-AUTHZ-ORGANIZATION-SCOPE-001` introduced unreachable from here.
      (assignment.organization_id ?? "") === form.organizationId,
  );

  return (
    <form
      style={{ padding: "0 18px 18px" }}
      onSubmit={(event) => {
        event.preventDefault();
        if (already !== undefined) return;
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<RoleAssignment>(
              "/authorization/assignments",
              {
                principal_id: form.principalId,
                role_id: form.roleId,
                // Absent, not null: the API reads a missing organization as
                // "the whole tenant", and sending null would be a second
                // spelling of the same fact.
                ...(form.organizationId === "" ? {} : { organization_id: form.organizationId }),
              },
              { correlationId },
            ),
          "Assigning a role",
          "Assigned.",
        )
          .then((result) => {
            setOutcome(result);
            if (result.tone === "ok") {
              setForm({ principalId: "", roleId: "", organizationId: "" });
              setLooking("");
              onAssigned();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>
        Give a principal a role
      </h3>
      <PrincipalPicker
        id="assign-principal"
        label="Principal"
        looking={looking}
        chosen={form.principalId}
        onType={(text) => {
          setLooking(text);
          setForm({ ...form, principalId: text });
        }}
        onChoose={(person) => {
          setLooking(person.display_name);
          setForm({ ...form, principalId: person.user_id });
        }}
      />
      <div className="nx-field">
        <label htmlFor="assign-role">Role</label>
        {assignable.length > 0 ? (
          <select
            id="assign-role"
            className="nx-select"
            required
            value={form.roleId}
            onChange={(event) => {
              setForm({ ...form, roleId: event.target.value });
            }}
          >
            <option value="">Choose a role</option>
            {assignable.map((role) => (
              <option key={role.role_id} value={role.role_id}>
                {role.display_name}
              </option>
            ))}
          </select>
        ) : (
          <input
            id="assign-role"
            className="nx-input"
            required
            value={form.roleId}
            onChange={(event) => {
              setForm({ ...form, roleId: event.target.value });
            }}
          />
        )}
        {/* One or the other, never both: a list the platform says is not all of
            them cannot also be evidence that there is nothing to choose. The
            truncation is the stronger statement, so it is the one that is
            made. */}
        {roles.hasMore ? (
          <MoreRolesThanListed />
        ) : assignable.length === 0 ? (
          <span className="nx-hint">
            {whyNothingToChoose(session.may("authorization.role.read"), roles)}
          </span>
        ) : null}
      </div>
      {/* Only when there is something to choose. A caller who belongs to no
          organization has no organization to name, and a disabled control
          saying so would be furniture — the appointment is tenant-wide either
          way, which is what sending nothing means. */}
      {organizations.length > 0 ? (
        <div className="nx-field">
          <label htmlFor="assign-organization">Organization</label>
          <select
            id="assign-organization"
            className="nx-select"
            value={form.organizationId}
            onChange={(event) => {
              setForm({ ...form, organizationId: event.target.value });
            }}
          >
            <option value="">The whole tenant</option>
            {organizations.map((organization) => (
              <option key={organization.organization_id} value={organization.organization_id}>
                {organization.display_name}
              </option>
            ))}
          </select>
          <span className="nx-hint">
            Which organization this appointment is for. It decides how far the role's
            organization-scoped grants reach, and nothing else: a tenant-scoped grant in the same
            role is unaffected, because how far a grant reaches is stated on the grant. Only
            organizations you belong to are listed — Nexus publishes no directory of the rest.
          </span>
        </div>
      ) : null}
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Assign
      </button>
      {already !== undefined ? (
        <p className="nx-hint" style={{ marginTop: 8 }}>
          That principal already holds this role
          {form.organizationId === "" ? " across the whole tenant" : " in that organization"}. Nexus
          refuses a second active assignment of an authority already held; this screen says so
          before asking for it.
        </p>
      ) : null}
      <Result outcome={outcome} />
    </form>
  );
}

function Assignments({
  roles,
  assignments,
  onChanged,
}: {
  roles: PagedCollection<Role>;
  assignments: PagedCollection<RoleAssignment>;
  onChanged: () => void;
}) {
  const session = useSession();
  const mayRead = session.may("authorization.role_assignment.read");
  const mayAssign = session.may("authorization.role_assignment.assign");
  const mayRevoke = session.may("authorization.role_assignment.revoke");
  const [busy, setBusy] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const roleOf = (roleId: string) => roles.items.find((role) => role.role_id === roleId);

  return (
    <Card
      title="Who holds what"
      actions={
        mayRead ? (
          <button type="button" className="nx-button" onClick={assignments.refresh}>
            Refresh
          </button>
        ) : null
      }
      bodyless
    >
      {mayRead ? (
        <>
          <Show
            resource={assignments.state}
            what="who holds which role"
            onRetry={assignments.refresh}
            empty={() =>
              assignments.items.length === 0 ? (
                <Empty
                  title="Nobody holds anything."
                  hint="Every principal in this tenant is denied by default."
                />
              ) : null
            }
          >
            {() => (
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <caption className="nx-visually-hidden">Role assignments in this tenant</caption>
                  <thead>
                    <tr>
                      <th scope="col">Principal</th>
                      <th scope="col">Role</th>
                      <th scope="col">State</th>
                      <th scope="col">Granted</th>
                      {mayRevoke ? <th scope="col">End</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.items.map((assignment) => {
                      const role = roleOf(assignment.role_id);
                      const isRoot = role?.key === ROOT_ROLE;
                      // Ended elsewhere, by a different authority — the
                      // ordinary route refuses every administrative role.
                      const isAppointment =
                        role !== undefined && !isRoot && isAdministrative(role);
                      return (
                        <tr key={assignment.assignment_id}>
                          <td>
                            <Identifier value={assignment.principal_id} />
                          </td>
                          <td>
                            {role === undefined ? (
                              <Identifier value={assignment.role_id} />
                            ) : (
                              <>
                                <span className="nx-table__primary">{role.display_name}</span>
                                <span className="nx-table__secondary" style={{ display: "block" }}>
                                  <Mono>{role.key}</Mono>
                                </span>
                                {isAdministrative(role) ? (
                                  <span style={{ display: "block", marginTop: 4 }}>
                                    <KindOf role={role} />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </td>
                          <td>
                            <Pill tone={assignment.active ? "ok" : "quiet"}>
                              {assignment.active ? "active" : "ended"}
                            </Pill>
                          </td>
                          <td>{when(assignment.granted_at)}</td>
                          {mayRevoke ? (
                            <td>
                              {!assignment.active ? (
                                <span className="nx-hint">—</span>
                              ) : isRoot ? (
                                <span className="nx-hint">
                                  Root of trust — not ended here
                                </span>
                              ) : isAppointment ? (
                                <span className="nx-hint">
                                  Administrative appointment — ended under Administrative
                                  appointments
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="nx-button"
                                  disabled={busy === assignment.assignment_id}
                                  onClick={() => {
                                    setBusy(assignment.assignment_id);
                                    setOutcome(null);
                                    void change(
                                      (correlationId) =>
                                        api.post(
                                          `/authorization/assignments/${assignment.assignment_id}/revocation`,
                                          {},
                                          { correlationId },
                                        ),
                                      "Ending an assignment",
                                      "Ended. The record that it existed stays.",
                                    )
                                      .then((result) => {
                                        setOutcome(result);
                                        if (result.tone === "ok") {
                                          assignments.refresh();
                                          onChanged();
                                        }
                                      })
                                      .finally(() => {
                                        setBusy(null);
                                      });
                                  }}
                                >
                                  End
                                </button>
                              )}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Show>
          <More
            collection={assignments}
            label="Show more assignments"
            hint="This is one page of who holds what, not all of it."
            placement="bodyless"
          />
          <div style={{ padding: "0 18px" }}>
            <Result outcome={outcome} />
          </div>
        </>
      ) : (
        <div className="nx-card__body">
          <Forbidden what="who holds which role" />
          <p className="nx-hint">
            Reaching this page is not permission to read assignments. It is a separate authority,
            and it was not granted to you.
          </p>
        </div>
      )}

      {mayAssign ? (
        <Assign roles={roles} held={assignments.items} onAssigned={assignments.refresh} />
      ) : null}
    </Card>
  );
}

// ── who administers ───────────────────────────────────────────────────────

interface AdministrativeAuthority {
  roleKey: string;
  title: string;
  /** What the authority decides, in one sentence. */
  decides: string;
  /** Why there is no role to appoint to, when there is none. */
  undefinedRole: string;
  /**
   * Where appointing goes when the role need not be read first.
   *
   * The delegated administrator is appointed through the generic route, which
   * names the role, so it has none. The two root-appointed authorities each
   * have a dedicated route that defines the role if the estate predates it and
   * appoints in the same act, so it works before the role exists and names only
   * the person.
   */
  appointPath: string | null;
}

const AUTHORITIES: AdministrativeAuthority[] = [
  {
    roleKey: DELEGATED_ROLE,
    title: "Delegated authorization administrator",
    decides:
      "Decides what principals may do: defines roles, grants operations and assigns ordinary roles. It cannot appoint administrators and cannot create service identities.",
    undefinedRole:
      "This role is not among the roles read here. It is established by deployment, and this screen needs its identifier to appoint anybody to it.",
    appointPath: null,
  },
  {
    roleKey: ACCESS_POLICY_ADMINISTRATOR_ROLE,
    title: "Access policy administrator",
    decides:
      "Decides which restricted data — such as personal data — the operations principals hold may reach, by drafting, activating and retiring tenant-wide access policies. It cannot assign roles and cannot create service identities.",
    undefinedRole:
      "Not yet defined in this tenant. Appointing the first holder defines it from the platform's manifest, as part of the same act.",
    appointPath: "/authorization/administrative-assignments/access-policy-administrator",
  },
  {
    roleKey: SERVICE_IDENTITY_ADMINISTRATOR_ROLE,
    title: "Service identity administrator",
    decides:
      "Decides which services exist to authenticate: registers service identities and issues, rotates and revokes their credentials. It cannot grant any service — or anybody — a role.",
    undefinedRole:
      "Not yet defined in this tenant. Appointing the first holder defines it from the platform's manifest, as part of the same act.",
    appointPath: "/authorization/administrative-assignments/service-identity-administrator",
  },
];

function AppointmentSection({
  authority,
  roles,
  assignments,
  onChanged,
}: {
  authority: AdministrativeAuthority;
  roles: PagedCollection<Role>;
  assignments: PagedCollection<RoleAssignment>;
  onChanged: () => void;
}) {
  const session = useSession();
  const mayAssign = session.may(ADMINISTRATIVE_ASSIGN);
  const mayRevoke = session.may(ADMINISTRATIVE_REVOKE);
  const mayReadRoles = session.may("authorization.role.read");
  const mayReadHolders = session.may("authorization.role_assignment.read");
  const [looking, setLooking] = useState("");
  const [principalId, setPrincipalId] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const role = roles.items.find((candidate) => candidate.key === authority.roleKey);
  const holders =
    role === undefined
      ? []
      : assignments.items.filter(
          (assignment) => assignment.active && assignment.role_id === role.role_id,
        );
  const already = holders.find((assignment) => assignment.principal_id === principalId);
  const headingId = `appointment-${authority.roleKey}`;

  /** Why no holder can be listed — said from what was read, never guessed. */
  function holdersUnknown(): string | null {
    if (!mayReadHolders) {
      return "Who holds this authority is read from who holds what, which is a separate authority you do not hold.";
    }
    if (!mayReadRoles) {
      return "Which role this is cannot be read here: reading which roles exist is a separate authority you do not hold.";
    }
    if (roles.state.kind !== "ready") return "The roles have not been read, so neither have its holders.";
    if (role === undefined) {
      return roles.hasMore
        ? "This role is not among the roles read so far. Show more of them under Roles above."
        : authority.undefinedRole;
    }
    return null;
  }
  const unknown = holdersUnknown();
  const cannotAppoint = authority.appointPath === null && role === undefined;

  return (
    <section aria-labelledby={headingId} style={{ padding: "0 18px 18px" }}>
      <h3 id={headingId} style={{ fontSize: "var(--nx-text-base)", margin: "0 0 6px" }}>
        {authority.title}
      </h3>
      <p className="nx-hint" style={{ margin: "0 0 10px" }}>
        {authority.decides}
      </p>

      {unknown !== null ? (
        <p className="nx-hint">{unknown}</p>
      ) : (
        <Show
          resource={assignments.state}
          what={`who holds ${authority.title.toLowerCase()}`}
          onRetry={assignments.refresh}
          empty={() =>
            holders.length === 0 ? (
              <p className="nx-hint">
                Nobody holds this authority
                {assignments.hasMore ? " among the appointments read so far" : ""}.
              </p>
            ) : null
          }
        >
          {() => (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <caption className="nx-visually-hidden">
                  Current holders of {authority.title}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Holder</th>
                    <th scope="col">Appointed</th>
                    {mayRevoke ? <th scope="col">End</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {holders.map((assignment) => (
                    <tr key={assignment.assignment_id}>
                      <td>
                        <Identifier value={assignment.principal_id} />
                        {assignment.principal_id === session.principal.user_id ? (
                          <span className="nx-hint"> (you)</span>
                        ) : null}
                      </td>
                      <td>{when(assignment.granted_at)}</td>
                      {mayRevoke ? (
                        <td>
                          <button
                            type="button"
                            className="nx-button"
                            disabled={busy === assignment.assignment_id}
                            onClick={() => {
                              setBusy(assignment.assignment_id);
                              setOutcome(null);
                              void change(
                                (correlationId) =>
                                  api.post(
                                    `/authorization/administrative-assignments/${assignment.assignment_id}/revocation`,
                                    {},
                                    { correlationId },
                                  ),
                                `Ending an appointment as ${authority.title.toLowerCase()}`,
                                `Appointment as ${authority.title.toLowerCase()} ended. Any other appointment this person holds is unchanged.`,
                              )
                                .then((result) => {
                                  setOutcome(result);
                                  if (result.tone === "ok") onChanged();
                                })
                                .finally(() => {
                                  setBusy(null);
                                });
                            }}
                          >
                            End appointment
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Show>
      )}
      {unknown === null && assignments.hasMore ? (
        <p className="nx-hint">
          Holders are listed from the appointments read so far. Show more under Who holds what to
          read the rest.
        </p>
      ) : null}

      {mayAssign ? (
        cannotAppoint ? (
          <p className="nx-hint">
            Appointing needs this role&apos;s identifier, and it has not been read here.
          </p>
        ) : (
          <form
            style={{ marginTop: 10 }}
            onSubmit={(event) => {
              event.preventDefault();
              if (already !== undefined) return;
              setBusy("appoint");
              setOutcome(null);
              void change(
                (correlationId) =>
                  authority.appointPath === null
                    ? api.post<RoleAssignment>(
                        "/authorization/administrative-assignments",
                        { principal_id: principalId, role_id: role?.role_id ?? "" },
                        { correlationId },
                      )
                    : // The dedicated route: it names the person and nothing
                      // else, and defines the role first if this estate
                      // predates it.
                      api.post<RoleAssignment>(
                        authority.appointPath,
                        { principal_id: principalId },
                        { correlationId },
                      ),
                `Appointing a ${authority.title.toLowerCase()}`,
                `Appointed as ${authority.title.toLowerCase()}. No other appointment was changed.`,
              )
                .then((result) => {
                  setOutcome(result);
                  if (result.tone === "ok") {
                    setLooking("");
                    setPrincipalId("");
                    onChanged();
                  }
                })
                .finally(() => {
                  setBusy(null);
                });
            }}
          >
            <PrincipalPicker
              id={`appoint-${authority.roleKey}`}
              label={`Person to appoint as ${authority.title.toLowerCase()}`}
              looking={looking}
              chosen={principalId}
              onType={(text) => {
                setLooking(text);
                setPrincipalId(text);
              }}
              onChoose={(person) => {
                setLooking(person.display_name);
                setPrincipalId(person.user_id);
              }}
            />
            <button
              type="submit"
              className="nx-button nx-button--primary"
              disabled={busy === "appoint"}
            >
              Appoint {authority.title.toLowerCase()}
            </button>
            {already !== undefined ? (
              <p className="nx-hint" style={{ marginTop: 8 }}>
                That person already holds this authority. Nexus refuses a second active appointment
                of an authority already held; this screen says so before asking for it.
              </p>
            ) : null}
          </form>
        )
      ) : null}
      <Result outcome={outcome} />
    </section>
  );
}

/**
 * Who administers, which restricted data is reachable, and who may create
 * principals — three authorities, appointed by the root and by nobody else.
 *
 * Offered on the root's own capabilities, never on the seven a delegate holds:
 * holding those has never answered "may I appoint administrators?", and the
 * delegation boundary is exactly that it does not.
 */
function AdministrativeAppointments({
  roles,
  assignments,
  onChanged,
}: {
  roles: PagedCollection<Role>;
  assignments: PagedCollection<RoleAssignment>;
  onChanged: () => void;
}) {
  return (
    <Card title="Administrative appointments" bodyless>
      <div className="nx-card__body">
        <p style={{ margin: 0 }}>
          Three distinct authorities, each appointed and ended on its own: who holds which
          operation (the delegated authorization administrator), which restricted data those
          operations reach (the access policy administrator), and which applications exist to hold
          them (the service identity administrator). They are different questions, so they are
          different appointments. Somebody holding two holds two appointments, not one wider role:
          ending one leaves the others exactly as they were.
        </p>
        <p className="nx-hint" style={{ margin: "8px 0 0" }}>
          Only the root of trust appoints or ends these. Every appointment is tenant-wide and is
          recorded in the audit trail on its own.
        </p>
      </div>
      {AUTHORITIES.map((authority) => (
        <AppointmentSection
          key={authority.roleKey}
          authority={authority}
          roles={roles}
          assignments={assignments}
          onChanged={onChanged}
        />
      ))}
    </Card>
  );
}

// ── the page ──────────────────────────────────────────────────────────────

export function Authorization() {
  const session = useSession();
  const roles = useCollection<Role>(
    "/authorization/roles",
    session.may("authorization.role.read"),
  );
  // Read once for the page, because two sections answer from it: who holds
  // what, and who holds each administrative authority. Two reads of the same
  // collection could disagree about who holds an appointment.
  const assignments = useCollection<RoleAssignment>(
    "/authorization/assignments",
    session.may("authorization.role_assignment.read"),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = roles.items.find((role) => role.role_id === selectedId) ?? null;
  const mayAppoint = session.may(ADMINISTRATIVE_ASSIGN) || session.may(ADMINISTRATIVE_REVOKE);

  return (
    <>
      <Banner title="Every section here is authorized on its own.">
        Reading which roles exist, reading what a role grants and reading who holds what are three
        different authorities. Nothing on this page is offered because something else was.
      </Banner>
      <div style={{ display: "grid", gap: 18 }}>
        <Roles roles={roles} selectedId={selectedId} onSelect={setSelectedId} />
        <Grants role={selected} />
        <Assignments roles={roles} assignments={assignments} onChanged={roles.refresh} />
        {mayAppoint ? (
          <AdministrativeAppointments
            roles={roles}
            assignments={assignments}
            onChanged={() => {
              assignments.refresh();
              roles.refresh();
            }}
          />
        ) : null}
      </div>
    </>
  );
}
